import { useState } from "react";
import { Vote, Check, X, Loader2, Gavel, RotateCcw, Mailbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { nb } from "@/lib/format";

const CHOICE_LABEL = { for: "For", mot: "Mot", avstar: "Avstår" };

function ChoiceBtns({ current, onPick, disabled, testidPrefix }) {
  return (
    <div className="flex gap-1.5">
      {["for", "mot", "avstar"].map((c) => (
        <button
          key={c}
          type="button"
          data-testid={`${testidPrefix}-${c}`}
          disabled={disabled}
          onClick={() => onPick(c)}
          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
            current === c
              ? c === "for"
                ? "border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : c === "mot"
                ? "border-red-500 bg-red-500/15 text-red-700 dark:text-red-300"
                : "border-foreground/40 bg-muted text-foreground"
              : "border-border hover:bg-muted"
          }`}
        >
          {CHOICE_LABEL[c]}
        </button>
      ))}
    </div>
  );
}

export function GfVoting({ meetingId, item, gf, onChange }) {
  const [maj, setMaj] = useState("alminnelig");
  const [busy, setBusy] = useState(false);
  const vote = item.vote;
  const isChair = !!gf?.is_chair;
  const blocs = gf?.blocs || [];
  const myId = gf?.my_holder_id;
  const myBloc = blocs.find((b) => b.id === myId);
  const registerReady = (gf?.register || []).length > 0;

  const call = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/meetings/${meetingId}/agenda/${item.id}/vote/${path}`, body || {});
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const prevotes = item.prevotes || {};
  const prevoteList = Object.entries(prevotes);
  const prevoteCount = prevoteList.length;
  const totalSh = (gf?.register || []).length;
  const remaining = totalSh ? Math.max(0, totalSh - prevoteCount) : 0;
  const choiceCls = (c) =>
    c === "for"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : c === "mot"
      ? "bg-red-500/15 text-red-700 dark:text-red-300"
      : "bg-muted text-foreground";
  const prevoteOverview =
    prevoteCount === 0 ? null : isChair ? (
      <details className="rounded-md border border-border/60 bg-card p-2.5" data-testid={`gf-prevote-panel-${item.id}`}>
        <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-foreground">
          <Mailbox className="h-3.5 w-3.5 text-primary" />
          {nb(prevoteCount)} forhåndsstemme{prevoteCount === 1 ? "" : "r"} mottatt
          {totalSh ? <span className="text-muted-foreground"> · {nb(remaining)} av {nb(totalSh)} aksjonærer gjenstår</span> : null}
        </summary>
        <div className="mt-2 space-y-1 border-t border-border/60 pt-2" data-testid={`gf-prevote-list-${item.id}`}>
          {prevoteList.map(([id, pv]) => (
            <div key={id} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate">{pv.name}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${choiceCls(pv.choice)}`}>{CHOICE_LABEL[pv.choice] || pv.choice}</span>
            </div>
          ))}
        </div>
      </details>
    ) : (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`gf-prevote-count-${item.id}`}>
        <Mailbox className="h-3.5 w-3.5 text-primary" />
        {nb(prevoteCount)} forhåndsstemme{prevoteCount === 1 ? "" : "r"} mottatt – telles automatisk med når avstemningen åpnes.
      </p>
    );

  if (!registerReady) {
    return (
      <div className="mt-3 space-y-2" data-testid={`gf-noreg-${item.id}`}>
        {prevoteOverview}
        <div className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          Bygg aksjonærregisteret i fanen «Aksjonærer &amp; fullmakter» for å stemme.
        </div>
      </div>
    );
  }

  const votesById = Object.fromEntries(blocs.map((b) => [b.id, b.effective_votes]));
  const ballots = vote?.ballots || {};
  const tally = { for: 0, mot: 0, avstar: 0 };
  Object.entries(ballots).forEach(([hid, c]) => { tally[c] = (tally[c] || 0) + (votesById[hid] || 0); });
  const castCount = Object.keys(ballots).length;

  if (!vote) {
    return (
      <div className="mt-3 rounded-lg border border-border/60 bg-muted/30 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Vote className="h-4 w-4" /> Avstemming</span>
          {isChair ? (
            <>
              <Select value={maj} onValueChange={setMaj}>
                <SelectTrigger data-testid={`gf-majority-${item.id}`} className="h-8 w-[210px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alminnelig">Alminnelig flertall</SelectItem>
                  <SelectItem value="kvalifisert">Kvalifisert flertall (2/3)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => call("open", { majority: maj })} data-testid={`gf-open-vote-${item.id}`}>
                <Gavel className="h-4 w-4" /> Åpne avstemming
              </Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Ikke åpnet ennå.</span>
          )}
        </div>
        {prevoteOverview ? <div className="mt-2">{prevoteOverview}</div> : null}
      </div>
    );
  }

  if (vote.status === "closed") {
    const vedtatt = vote.outcome === "vedtatt";
    return (
      <div className="mt-3 rounded-lg border border-border/60 bg-muted/30 p-3" data-testid={`gf-result-${item.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${vedtatt ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-red-500/15 text-red-700 dark:text-red-300"}`}>
            {vedtatt ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {vedtatt ? "Vedtatt" : "Forkastet"}
          </span>
          <span className="text-xs text-muted-foreground">
            For {nb(vote.for_votes)} · Mot {nb(vote.against_votes)} · Avstår {nb(vote.abstain_votes)} (av {nb(vote.total_represented)} stemmer, {vote.majority === "kvalifisert" ? "2/3" : "alminnelig"})
          </span>
          {isChair ? (
            <Button size="sm" variant="ghost" className="ml-auto gap-1.5 text-xs" disabled={busy} onClick={() => call("reset")} data-testid={`gf-reset-vote-${item.id}`}>
              <RotateCcw className="h-3.5 w-3.5" /> Ny avstemming
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-primary/25 bg-primary/[0.04] p-3" data-testid={`gf-open-${item.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-lime px-2.5 py-0.5 text-xs font-semibold text-lime-foreground"><Vote className="h-3.5 w-3.5" /> Avstemming åpen</span>
        <span className="text-xs text-muted-foreground">{vote.majority === "kvalifisert" ? "Kvalifisert (2/3)" : "Alminnelig flertall"} · {castCount} avgitt</span>
        <span className="ml-auto text-xs text-muted-foreground">For {nb(tally.for)} · Mot {nb(tally.mot)} · Avstår {nb(tally.avstar)}</span>
      </div>
      {vote.prevote_count > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`gf-prevote-included-${item.id}`}>
          <Mailbox className="h-3.5 w-3.5 text-primary" /> {nb(vote.prevote_count)} forhåndsstemme{vote.prevote_count === 1 ? "" : "r"} er lagt inn automatisk (kan overstyres nedenfor).
        </p>
      ) : null}

      {myBloc ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-card p-2.5">
          <span className="text-xs font-medium">Din stemme <span className="text-muted-foreground">({nb(myBloc.effective_votes)} stemmer)</span></span>
          <ChoiceBtns current={ballots[myId]} disabled={busy} onPick={(c) => call("ballot", { choice: c })} testidPrefix={`gf-myvote-${item.id}`} />
        </div>
      ) : null}

      {isChair ? (
        <div className="space-y-2 rounded-md border border-border/60 bg-card p-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Møteleder – registrer utfall</span>
            <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" disabled={busy} onClick={() => call("unanimous", { choice: "for" })} data-testid={`gf-unanimous-${item.id}`}>Alle stemmer for</Button>
          </div>
          <div className="space-y-1.5">
            {blocs.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2">
                <span className="truncate text-xs">{b.name} <span className="text-muted-foreground">({nb(b.effective_votes)})</span></span>
                <ChoiceBtns current={ballots[b.id]} disabled={busy} onPick={(c) => call("ballot", { holder_id: b.id, choice: c })} testidPrefix={`gf-bloc-${item.id}-${b.id}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-1">
            <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => call("close")} data-testid={`gf-close-vote-${item.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gavel className="h-4 w-4" />} Avslutt &amp; fastsett vedtak
            </Button>
          </div>
        </div>
      ) : !myBloc ? (
        <p className="text-xs text-muted-foreground">Du har ikke stemmerett i dette møtet.</p>
      ) : null}
    </div>
  );
}
